
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Button, Hide, Show, Text, VStack } from '@chakra-ui/react'
import { useAuth } from 'context'
import { AuthCreatePasswordFormType, AuthDecodedUserType } from 'context/types'
import { CREATE_NEW_PASSWORD_SCHEMA } from 'context/validations'
import { Formik } from 'formik'
import jwt_decode from 'jwt-decode'
import { themeColors } from 'theme'

import { InputControl, SubmitButton } from 'components/InputCustom'
import { WicarLogo, WicarLogoSmall } from 'components/SVGS'
import { customToast } from 'components/ToastCustom'

type CreateNewPasswordParams = {
  newToken: string
}


export const CreatePassword = () => {
  const { createNewPassword, clearError, authState: { loading, error } } = useAuth()
  const navigate = useNavigate()
  const { newToken } = useParams<CreateNewPasswordParams>()
  const {
    container: { gradientColor: { gradientOne, gradientTwo, gradientThree } },
  } = themeColors

  const initialValues: AuthCreatePasswordFormType = {
    newPassword: '',
    confirmNewPassword: '',
    token: newToken ?? '',
  }

  const handleOnSubmit = async (values: AuthCreatePasswordFormType) => {
    const success = await createNewPassword({ password: values.newPassword }, values.token)

    if (success) {
      customToast.showToast({ description: `Contraseña creada con éxito`, status: 'success' })
      navigate('/')
    }
  }

  useEffect(() => {
    if (error)
      customToast.showToast({ description: error })
    clearError()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (newToken)
      try {
        const token = decodeURIComponent(newToken)

        const decodedUser: AuthDecodedUserType = jwt_decode(token)

        const date = new Date()

        decodedUser.exp * 1000 < date.getTime() && navigate('/')

      } catch (error) {
        return navigate('/')
      }
  }, [newToken])

  useEffect(() => {
    localStorage.clear()
  }, [])

  return (
    <VStack
      minH={'100vh'}
      minW={'100vw'}
      bgGradient={`linear(to-b, ${gradientOne}, ${gradientTwo}, ${gradientThree})`}>
      <Formik
        enableReinitialize
        validateOnMount
        initialValues={initialValues}
        onSubmit={handleOnSubmit}
        validationSchema={CREATE_NEW_PASSWORD_SCHEMA}>
        <VStack
          flex={1}
          mx="auto"
          w={'full'}
          maxW={{ base: '90%', md: '550px' }}
          h={'fit-content'}
          rounded='2xl'
          p={{ base: 2, md: 4 }}
          justifyContent='center'
          alignItems='center'
          spacing={{ base: 4, md: '70px' }}>
          <VStack>
            <Show below='md'>
              <WicarLogoSmall onClick={() => navigate('/')} width={50} height={50} />
            </Show>
            <Hide below='md'>
              <WicarLogo onClick={() => navigate('/')} width={200} height={50} />
            </Hide>
          </VStack>
          <VStack w={'full'} justifyContent={'center'} alignItems={'center'} spacing={'24px'}>
            <VStack>
              <Text variant={'LG_SB'} textAlign={'center'}>Restablece tu contraseña</Text>
              <Text textAlign={'center'}>
                Crea una contraseña nueva
              </Text>
            </VStack>
            <VStack w={'full'} maxW={'330px'} spacing={'32px'}>
              <InputControl
                inputType='password'
                inputProps={{ placeholder: 'Ingresa la nueva contraseña' }}
                name='newPassword'
                label='Nueva contraseña (Mínimo 6 dígitos)'
                isDisabled={loading}
              />
              <InputControl
                inputType='password'
                inputProps={{ placeholder: 'Confirmar contraseña' }}
                name='confirmNewPassword'
                label='Confirma la nueva contraseña'
                isDisabled={loading}
              />
              <SubmitButton w={'full'} loadingText={'Creando...'} isLoading={loading}>
                Crear contraseña
              </SubmitButton>
              <Button w={'full'} variant={'secondary'} disabled={loading} onClick={() => navigate('/')}>
                Volver
              </Button>
            </VStack>
          </VStack>
        </VStack>
      </Formik>
    </VStack>
  )
}
